"use client";

import React from "react";

import Image from "next/image";
import DecorationLine from "@/components/global/ui/DecorationLine";
import CornerChip from "@/components/global/ui/CornerChip";
import NumberedColoredList from "@/components/global/ui/NumberedColoredList";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";

import { PageHomeNewSchema } from "@/sanity/pages/PageHomeNewTemplate";

import bastiSignature from "@/public/home-new/basti-signature.svg";
import letterStamp from "@/public/home-new/letter-stamp.png";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

export default function LetterSection({
  data,
  className,
}: {
  data: PageHomeNewSchema;
  className?: string;
}) {
  return (
    <section className={cn("relative overflow-clip", className)}>
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />
      <DecorationLine className="left-0 top-0" orientation="horizontal" />

      <div className="container flex flex-col items-center">
        <div className="w-[min(124.5rem,100%)] flex flex-col items-center relative py-[10rem] lg:py-[20rem]">
          <DecorationLine
            className="left-0 top-0 hidden lg:block"
            orientation="vertical"
          />
          <DecorationLine
            className="right-0 top-0 hidden lg:block"
            orientation="vertical"
          />

          <div className="w-[min(111rem,100%)] p-[2rem] sm:p-[4rem] lg:p-[7rem] bg-white relative border-t-[4px] lg:border-t-[6px] border-t-[#D9F9B9]">
            <CornerChip
              className="absolute -left-[1px] bottom-full prose-img:!w-[2rem] lg:px-[4rem] lg:min-h-[6rem]"
              image={data?.letterChip?.icon}
              text={data?.letterChip?.text}
            />

            {data?.letterText1 && (
              <div className="richtext richtext-start prose-headings:mb-[3rem] prose-strong:text-black prose-strong:font-normal mb-[1.5rem]">
                <PortableText
                  value={data.letterText1}
                  components={richTextComponent}
                />
              </div>
            )}

            <NumberedColoredList
              className="mb-[3rem]"
              items={data?.letterList1}
            />

            {data?.letterText2 && (
              <div className="richtext richtext-start prose-headings:mb-[3rem] prose-strong:text-black prose-strong:font-normal mb-[1.5rem]">
                <PortableText
                  value={data.letterText2}
                  components={richTextComponent}
                />
              </div>
            )}

            <hr className="w-full border-t border-t-[#202223]/[.15] my-[2rem] lg:my-[3rem]" />

            {data?.letterQuote?.title && data?.letterQuote?.text && (
              <div className="w-full flex mb-[3rem]">
                <div className="w-[4.125rem] flex-shrink-0 flex justify-center items-center bg-[#052920]/20">
                  <svg
                    className="w-[2.125rem]"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.2734 16L13.2086 6.72119H10.0899V0H17V5.88848L13.3309 16H10.2734ZM0.183453 16L3.11871 6.72119H0V0H6.91007V5.88848L3.24101 16H0.183453Z"
                      fill="#052920"
                    />
                  </svg>
                </div>

                <div className="py-[1.5rem] pl-[1.5rem] pr-[2.5rem] lg:pr-[4rem] grow bg-[#052920]/10">
                  <p className="mb-0 font-geistmono text-black font-medium text-[1.75rem] uppercase">
                    {data.letterQuote.title}
                  </p>
                  <p className="mb-0 text-[2rem] leading-[1.3] text-black/60">
                    {data.letterQuote.text}
                  </p>
                </div>
              </div>
            )}

            {data?.letterText3 && (
              <div className="richtext richtext-start prose-headings:mb-[3rem] prose-strong:text-black prose-strong:font-normal mb-[1.5rem]">
                <PortableText
                  value={data.letterText3}
                  components={richTextComponent}
                />
              </div>
            )}

            <NumberedColoredList
              className="mb-[3rem]"
              items={data?.letterList2}
              numberBackgroundColor="bg-[#052920]/20"
              textBackgroundColor="bg-[#052920]/10"
            />

            {data?.letterText4 && (
              <div className="richtext richtext-start prose-headings:mb-[3rem] prose-strong:text-black prose-strong:font-normal mb-[1.5rem] prose-ul:mb-[2.5rem] prose-li:m-0 prose-li:text-[2.25rem] prose-li:text-black/60 prose-p:mb-[1.5rem] [&_::marker]:text-black/50">
                <PortableText
                  value={data.letterText4}
                  components={richTextComponent}
                />
              </div>
            )}

            <hr className="w-full lg:w-[73.25rem] border-t border-t-[#202223]/[.15] my-[2rem] lg:my-[3rem]" />

            <div className="flex items-end justify-between lg:-mt-[9rem]">
              <div className="flex flex-col items-center">
                <Image
                  className="!w-[20rem] lg:!w-[25rem]"
                  src={bastiSignature}
                  alt="basti signature"
                />

                <p className="mb-0 text-[1.75rem] lg:text-[2rem] text-black/50 font-geistmono leading-none uppercase -mt-[2rem]">
                  {data?.settings?.language === "en" ? "Founder" : "Gründer"} & CEO,
                  <br />
                  Trademania
                </p>
              </div>

              <Image
                className="!w-[20rem] lg:!w-[28.625rem]"
                src={letterStamp}
                alt="letter stamp"
              />
            </div>
          </div>

          {data?.letterCTA && (
            <CTACornerButton className="mt-[4rem]" data={data.letterCTA} />
          )}
        </div>
      </div>
    </section>
  );
}
